import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  Link,
  Button,
  Flex,
  Spinner,
} from "@chakra-ui/react";

/**
 * Shared preview modal.
 * Used by both SourceSection.jsx and OutputSection.jsx when the file name is clicked.
 *
 * Props:
 * - isOpen, onClose
 * - title: header label (file name)
 * - url: file_path to preview / open
 * - isImage: whether to attempt an image preview, otherwise show an "Open file" link
 */
const MediaPreviewModal = ({ isOpen, onClose, title, url, isImage = true }) => {
  // "loading" | "loaded" | "error"
  const [status, setStatus] = useState("loading");

  // Reset load state whenever a new file is opened, so a previous
  // failure/success doesn't leak into the next preview.
  useEffect(() => {
    if (isOpen && url && isImage) {
      setStatus("loading");
    }
  }, [isOpen, url, isImage]);

  const showImage = isImage && url && status !== "error";

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader noOfLines={2} pr={10} fontSize="md">
          {title || "Preview"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={2}>
          {!url ? (
            <Text color="gray.500" py={4}>
              No file available.
            </Text>
          ) : showImage ? (
            <Flex justify="center" align="center" minH="200px" position="relative">
              {status === "loading" && (
                <Flex position="absolute" direction="column" align="center" gap={2}>
                  <Spinner size="lg" color="blue.500" />
                  <Text fontSize="sm" color="gray.500">
                    Loading preview...
                  </Text>
                </Flex>
              )}
              <img
                key={url}
                src={url}
                alt={title || "preview"}
                referrerPolicy="no-referrer"
                onLoad={() => setStatus("loaded")}
                onError={() => setStatus("error")}
                style={{
                  maxHeight: "70vh",
                  width: "100%",
                  objectFit: "contain",
                  borderRadius: 8,
                  visibility: status === "loading" ? "hidden" : "visible",
                  position: status === "loading" ? "absolute" : "static",
                }}
              />
            </Flex>
          ) : (
            <Text color="gray.600" py={4}>
              {isImage
                ? "Couldn't load a preview for this file."
                : "Preview isn't available for this file type."}{" "}
              <Link href={url} isExternal color="blue.500" fontWeight="600">
                Open file
              </Link>
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          {url ? (
            <Button as={Link} href={url} isExternal variant="outline" size="sm">
              Open in new tab
            </Button>
          ) : null}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MediaPreviewModal;